import { useEffect, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import {
  LayoutDashboard, Users, CheckCircle2, Image as ImageIcon, HeartHandshake, Flag, Settings,
  Menu, ChevronsLeft, ChevronsRight, UserRound,
} from "lucide-react";
import { TopBar } from "../TopBar";
import { SignOutButton } from "../AccountMenu";
import { useAuth } from "../../context/AuthContext";
import { allowed } from "../../lib/adminApi";
import { useNoindex } from "../../hooks/useNoindex";
import { supabase } from "../../lib/supabase";

export const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, perm: null, end: true, id: "dashboard" },
  {
    to: "/admin/profiles", label: "User Management", icon: Users, perm: null, id: "users",
    children: [
      { to: "/admin/profiles", label: "All Profiles", end: true, countKey: "all" },
      { to: "/admin/profiles/brides", label: "Brides", countKey: "bride" },
      { to: "/admin/profiles/grooms", label: "Grooms", countKey: "groom" },
      { to: "/admin/profiles/incomplete", label: "Incomplete Registrations", countKey: "incomplete" },
    ],
  },
  { to: "/admin/approvals", label: "Profile Approval", icon: CheckCircle2, perm: "can_approve", id: "approvals" },
  { to: "/admin/media", label: "Photos & ID Review", icon: ImageIcon, perm: ["can_approve", "can_verify"], id: "media" },
  { to: "/admin/matches", label: "Matches & Shares", icon: HeartHandshake, perm: "can_share", id: "matches" },
  { to: "/admin/reports", label: "Reports & Flags", icon: Flag, perm: null, id: "reports" },
  { to: "/admin/settings", label: "Settings", icon: Settings, perm: "admin", id: "settings" },
];

const linkCls = (isActive) =>
  `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-[#7A0016] text-[#F5E6BA]" : "text-[#2C2424] hover:bg-[#F5E6BA]/60"}`;

function NavItems({ collapsed, onNavigate, counts }) {
  const auth = useAuth();
  return (
    <nav className="space-y-1 p-3">
      {NAV.filter((n) => allowed(auth, n.perm)).map(({ to, label, icon: Icon, end, id, children }) => (
        <div key={to}>
          <NavLink to={to} end={end || !!children} onClick={onNavigate} title={label} data-testid={`admin-nav-${id}`} className={({ isActive }) => linkCls(isActive && !children)}>
            <Icon className="h-4 w-4 shrink-0" />
            {!collapsed && <span className="truncate">{label}</span>}
          </NavLink>
          {children && !collapsed && (
            <div className="ml-4 mt-1 space-y-0.5 border-l border-[#D4AF37]/40 pl-3">
              {children.map((c) => (
                <NavLink
                  key={c.to}
                  to={c.to}
                  end={c.end}
                  onClick={onNavigate}
                  data-testid={`admin-nav-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) => `flex items-center justify-between rounded-md px-2 py-1.5 text-[13px] transition-colors ${isActive ? "bg-[#F5E6BA] font-semibold text-[#7A0016]" : "text-[#8C6A6C] hover:bg-[#F5E6BA]/50"}`}
                >
                  <span className="inline-flex items-center gap-1.5"><UserRound className="h-3 w-3" />{c.label}</span>
                  <span data-testid={`nav-count-${c.countKey}`} className="rounded-full bg-[#7A0016]/10 px-1.5 text-[11px] font-semibold text-[#7A0016]">{counts?.[c.countKey] ?? "-"}</span>
                </NavLink>
              ))}
            </div>
          )}
        </div>
      ))}
    </nav>
  );
}

export default function AdminShell() {
  useNoindex();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [counts, setCounts] = useState(null);
  useEffect(() => {
    (async () => {
      const [all, bride, groom, incomplete] = await Promise.all(["", "bride", "groom"].map(async (pf) => {
        let q = supabase.from("admin_profile_list").select("id", { count: "exact", head: true });
        if (pf) q = q.eq("profile_for", pf);
        const { count } = await q;
        return count || 0;
      }).concat(supabase.from("incomplete_registrations").select("user_id", { count: "exact", head: true }).then((r) => r.count || 0)));
      setCounts({ all, bride, groom, incomplete });
    })();
  }, []);
  const menuBtn = (
    <button type="button" onClick={() => setMobileOpen(true)} data-testid="admin-mobile-menu-button" aria-label="Open menu" className="rounded-full p-2 text-[#7A0016] hover:bg-[#F5E6BA]/60 lg:hidden">
      <Menu className="h-5 w-5" />
    </button>
  );
  return (
    <div className="rmm-bg min-h-screen">
      <TopBar right={<>{menuBtn}<SignOutButton /></>} />
      <div className="mx-auto flex max-w-[1600px]">
        <aside data-testid="admin-sidebar" className={`sticky top-[73px] hidden h-[calc(100vh-73px)] shrink-0 overflow-y-auto border-r border-[#D4AF37]/30 bg-white/70 lg:block ${collapsed ? "w-16" : "w-64"} transition-[width]`}>
          <NavItems collapsed={collapsed} counts={counts} />
          <button type="button" onClick={() => setCollapsed((c) => !c)} data-testid="admin-sidebar-collapse" className="mx-3 mb-3 flex items-center gap-2 rounded-lg px-3 py-2 text-xs text-[#8C6A6C] hover:bg-[#F4EFEA]">
            {collapsed ? <ChevronsRight className="h-4 w-4" /> : <><ChevronsLeft className="h-4 w-4" /> Collapse</>}
          </button>
        </aside>
        {mobileOpen && (
          <div className="fixed inset-0 z-[60] lg:hidden" data-testid="admin-mobile-nav">
            <button type="button" aria-label="Close menu" className="absolute inset-0 bg-black/30" onClick={() => setMobileOpen(false)} />
            <div className="absolute inset-y-0 left-0 w-72 overflow-y-auto bg-white shadow-xl">
              <NavItems collapsed={false} onNavigate={() => setMobileOpen(false)} counts={counts} />
            </div>
          </div>
        )}
        <main className="min-w-0 flex-1 px-4 py-6 sm:px-6 lg:px-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
